import { FormError, Input, Label } from "@simplicity/ui";
import type { ReactNode } from "react";
import type { PlacementKey } from "./onsiteModel";

interface FieldSpec {
  slug: string;
  label: string;
  placeholder: string;
}

// Placeholders are the onsite module's defaults, which a blank field falls back to. No machine
// type: the root module ignores `sftp_vm_machine_type` until SIM-2510 declares it.
const FIELDS: Record<PlacementKey, FieldSpec> = {
  region: { slug: "region", label: "Region", placeholder: "us-central1" },
  zone: { slug: "zone", label: "Zone", placeholder: "us-central1-a" },
};

function PlacementField({
  id,
  spec,
  value,
  error,
  hintId,
  onChange,
}: {
  id: string;
  spec: FieldSpec;
  value: string;
  error: string | undefined;
  hintId: string;
  onChange: (value: string) => void;
}) {
  const errorId = `${id}-error`;
  return (
    <div>
      <Label htmlFor={id} className="form-label">
        {spec.label}
      </Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={spec.placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${hintId} ${errorId}` : hintId}
        autoComplete="off"
      />
      {error && <FormError id={errorId}>{error}</FormError>}
    </div>
  );
}

/** Where the workspace's resources live, shared by the create and edit forms. */
export function PlacementFields<K extends PlacementKey>({
  idPrefix,
  keys,
  values,
  errors = {},
  onChange,
  notice,
}: {
  idPrefix: string;
  keys: readonly K[];
  values: NoInfer<Record<K, string>>;
  errors?: NoInfer<Partial<Record<K, string>>>;
  onChange: NoInfer<(key: K, value: string) => void>;
  notice?: ReactNode;
}) {
  const hintId = `${idPrefix}-placement-hint`;
  return (
    <div className="mb-4 grid gap-4 sm:grid-cols-2">
      {keys.map((key) => (
        <PlacementField
          key={key}
          id={`${idPrefix}-${FIELDS[key].slug}`}
          spec={FIELDS[key]}
          value={values[key]}
          error={errors[key]}
          hintId={hintId}
          onChange={(value) => onChange(key, value)}
        />
      ))}
      <p id={hintId} className="text-xs text-muted-foreground sm:col-span-2">
        Leave a field blank to use the default shown in it. The zone must be inside the region.
      </p>
      {notice}
    </div>
  );
}
